// From opening desyr's address to the chats (03-startup-session.md, Behaviour): the storage probe,
// config.json, then signed out (sign-in) or signed in (the client, loaded only now).
import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { idOnly, loadConfig, type Config } from '../core/config';
import { canStoreData, clearAllData } from '../core/device';
import { describe, type Failure } from '../core/errors';
import { guard, navigate, reopen, useAddress } from '../core/router';
import { keepSignedInPage, looksSignedIn, parseRoute, rememberReturn, SIGNED_OUT_PAGES, takeReturn, type Route } from '../core/routes';
import { leave, readSession, writeSession, type Session } from '../core/session';
import { sendSession } from '../core/worker';
import { setSignedIn } from '../design/theme';
import { takeAccountRequest } from '../signin/account';
import { activateAccount, defaultAccount, signedInAccount, startAccounts, switchTo, waiting } from '../accounts/store';
import { AutoSignIn, IdSignIn, Opening, YourCircles, type IdFailure } from '../id/IdCards';
import { DevicePage } from '../id/DeviceLink';
import { Invitations } from '../id/Invitations';
import { gone, listAfter, syncCircles } from '../id/circles';
import { anyDue } from '../id/sessions';
import { dropCircle } from '../id/Leave';
import { circleName } from '../accounts/accounts';
import { circlesCard, signOutOfId } from '../id/session';
import { finishSignIn, IdError } from '../id/oidc';
import { newRun, takeHop, takeSignInError } from '../id/hop';
import { actionWaiting, claimJump, dropAuto, endConfirmed, readId, readQueue, signedInDone, takeAfter, takeReturn as takeIdReturn } from '../id/store';
import { followSignOut, outByOtherTab } from '../id/tabs';
import { noteAfter } from '../id/circles';
import { endSwitching } from '../accounts/switching';
import { chooseCompany, found } from '../signin/company';
import { NotFound } from './NotFound';
import { guideOnHome } from '../install/install';
import { Broke, CantStart, ConfigFailed, NoStorage, OfflineStart, running, StartScreen } from './screens';

const SignedIn = lazy(() => import('../session/SignedIn'));

type Phase =
  | { name: 'starting' }
  | { name: 'no-storage' }
  | { name: 'config-failed'; failure: Failure }
  | { name: 'offline' }
  | { name: 'signed-out'; config: Config; idFailure?: IdFailure; deleted?: boolean; here?: boolean }
  /** The device link's page (36, Sign in on this device, v1.58.47): for everyone, never a jump. */
  | { name: 'device'; config: Config }
  /** Right after the desyr ID sign-in: "Opening your circles…" while the sessions call runs (36, v1.58.15). */
  | { name: 'opening'; config: Config }
  /** Signed out with desyr ID: its sign-in at once from the start screen, back to `page` (36, v1.58.51). */
  | { name: 'auto'; config: Config; page: string }
  /** desyr ID's "Your circles" (36), also while circles sign in one after the other. */
  | { name: 'circles'; config: Config; signInError?: boolean }
  | { name: 'signed-in'; config: Config; session: Session };

/** Catches what fails while drawing: `starting` shows "couldn't start" until the frame is shown, else "Something went wrong". */
export class Boundary extends Component<{ starting?: boolean; children: ReactNode }, { error?: unknown }> {
  state: { error?: unknown } = {};
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  render() {
    const { error } = this.state;
    if (error === undefined) return this.props.children;
    return this.props.starting && !running ? <CantStart error={error} onStartFresh={clearAllData} /> : <Broke error={error} />;
  }
}

export interface AppProps {
  /** `signedIn` is called with the new session after a successful sign-in. */
  signedOut: (config: Config, route: Route, signedIn: (s: Session) => void) => ReactNode;
}

/**
 * Signed out (05, Routes): the pages that need no session open; "/" and anything shaped like a
 * signed-in page (one to three parts) are remembered and lead to sign-in; longer unknown addresses
 * show the not-found page on its own.
 */
function SignedOutRoutes({ config, idFailure, deleted, here, render, onSignedIn }: { config: Config; idFailure?: IdFailure; deleted?: boolean; here?: boolean; render: AppProps['signedOut']; onSignedIn: (s: Session) => void }) {
  const [legacy, setLegacy] = useState(false);
  const address = useAddress();
  const path = location.pathname;
  const route = parseRoute(path);
  const toSignIn = route.page === 'root' || (route.page === 'not-found' ? looksSignedIn(path) : !SIGNED_OUT_PAGES.includes(route.page));
  useEffect(() => {
    if (!toSignIn) return;
    rememberReturn(address);
    navigate('/login', { replace: true });
  }, [toSignIn, address]);
  if (toSignIn) return null;
  if (route.page === 'not-found') return <NotFound alone />;
  // With desyr ID (36): its sign-in card, unless the old way was chosen while it still exists; after the move every sign-in address (39).
  if (config.desyrId && route.page === 'login' && (idOnly(config) || (!route.server && !(legacy && config.desyrId.legacy))))
    return <IdSignIn config={config} id={config.desyrId} failure={idFailure} note={deleted} here={here} onLegacy={() => setLegacy(true)} />;
  return render(config, route, onSignedIn);
}

export function App({ signedOut }: AppProps) {
  const [shownPhase, setPhase] = useState<Phase>({ name: 'starting' });
  const [attempt, setAttempt] = useState(0);
  // Signed out in another tab (36, Behaviour 4.4.2, v1.58.63): this tab's requests end without telling
  // the servers (that tab does), its dialogs close, nothing of the account stays: the card, no jump.
  const [, setOut] = useState(false);
  useEffect(() => followSignOut(() => (leave(), history.replaceState(null, '', '/login'), setOut(true))), []);
  const phase: Phase = outByOtherTab() && 'config' in shownPhase && shownPhase.name !== 'signed-out' ? { name: 'signed-out', config: shownPhase.config } : shownPhase;

  useEffect(() => {
    let live = true;
    const go = (p: Phase) => live && setPhase(p);
    (async () => {
      if (!(await canStoreData())) return go({ name: 'no-storage' });
      startAccounts(); // the default account, before anything reads the session (33)
      // Nobody signed in and no network for certain: nothing could be shown but this page (03).
      if (!readSession() && navigator.onLine === false) {
        go({ name: 'offline' });
        await new Promise((r) => addEventListener('online', r, { once: true }));
      }
      let config: Config;
      try {
        config = await loadConfig();
      } catch (e) {
        return go({ name: 'config-failed', failure: describe(e, 'start') });
      }
      if (location.pathname === '/id/device/') return go({ name: 'device', config });
      // Back from desyr ID's end-session (36, Behaviour 4.3, v1.58.63): only its `state` confirms the end.
      const ended = new URL(location.href);
      if (config.desyrId && location.pathname === '/login' && ended.searchParams.has('state')) {
        endConfirmed(ended.searchParams.get('state'));
        ended.searchParams.delete('state');
        history.replaceState(null, '', ended.pathname + ended.search);
      }
      // The account service's page way comes back to desyr's authorisation address (04).
      const fromAccountPage = takeAccountRequest(config, found());
      if (fromAccountPage) chooseCompany(fromAccountPage);
      // Back from desyr ID's sign-in (36, Behaviour 1.2): its address replaced at once; then "Your circles".
      let idFailure: IdError['reason'] | undefined;
      let opening = false;
      if (config.desyrId && location.pathname === '/id/done/') {
        const back = await finishSignIn(config.desyrId, new URL(location.href)).catch((e: unknown) => {
          idFailure = e instanceof IdError ? e.reason : 'failed';
          // Cancelled on desyr ID's page: the card, and the page it was for opens after the next sign-in (04 step 3).
          if (e instanceof IdError && e.page) keepSignedInPage(e.page);
          return '/login';
        });
        // Back never returns to desyr ID's pages (07, v1.58.27): a guard under the page.
        if (idFailure) history.replaceState(null, '', back);
        else guard(back), keepSignedInPage(back); // "Opening your circles…" ends there, not at Home (04 step 3, 05)
        // A sign-in for Leave or Delete lands at its dialog (39, Behaviour 1.7); cancelled, the dialog stays closed.
        if (idFailure) takeAfter('leave', 'delete', 'switch');
        else {
          signedInDone(); // a completed sign-in: the next sign-out marks the device again (v1.58.51)
          newRun(); // a fresh desyr ID sign-in: every circle not on this device once more
          if (!actionWaiting()) opening = circlesCard(true);
        }
      }
      // A circle's silent sign-in coming back (36, Behaviour 2.2, rule 6), before the page kept for it.
      if (config.desyrId) await takeHop();
      // Back from one of desyr ID's own pages (36, Behaviour 3.2), or from a Join's hop: the page the person came from.
      const back = config.desyrId && takeIdReturn();
      if (back) guard(back);
      // Back from the account service's error page (36, Behaviour 2.2, rule 8).
      const signInError = config.desyrId && takeSignInError(config);
      if (opening) return go({ name: 'opening', config });
      if (config.desyrId && readId() && !actionWaiting() && (signInError || circlesCard() || readQueue().length)) return (noteAfter(), go({ name: 'circles', config, signInError: signInError === 'general' }));
      const session = readSession();
      setSignedIn(!!session);
      // Starting signed out (36, v1.58.51): straight to desyr ID's sign-in, no card; the card only after
      // signing out, a cancel or failure, the loop guard, and while the old way is still offered.
      // "Sign in again" (`?id=go`) signs in whatever the marks say, as the person's own press, not a jump.
      if (!session && config.desyrId && !config.desyrId.legacy && !idFailure && !signInError) {
        const path = location.pathname;
        const route = parseRoute(path);
        const asked = new URLSearchParams(location.search).get('id') === 'go';
        const start = route.page === 'root' || (route.page === 'login' && !route.server && (!location.search || asked));
        const needsSession = !SIGNED_OUT_PAGES.includes(route.page) && (route.page !== 'not-found' || looksSignedIn(path));
        if (start || needsSession) {
          if (asked) dropAuto();
          if (asked || (await claimJump())) return go({ name: 'auto', config, page: start ? takeReturn() : path + location.search });
        }
      }
      // The sessions call with the list and the invitations, at every start (36, Behaviour 6.1; 37, Behaviour 1.6):
      // before desyr opens when a session from it ends within 10 minutes (at most 10 s), else at most 3 s.
      if (session && config.desyrId && readId()) {
        // A circle the list no longer holds goes quietly at the start (36, Behaviour 2.3; 39), before its
        // chat starts: its session may no longer answer at all. Unreachable, the start goes on.
        const outcome = await Promise.race([syncCircles(config), new Promise<undefined>((r) => setTimeout(r, anyDue() ? 10_000 : 3000))]);
        const answer = listAfter(outcome);
        if (answer && gone(answer.circles, [session.user_id]).length) return void (await dropCircle(session.user_id, { do: 'removed', name: circleName(session.user_id) }));
      }
      if (session) return go({ name: 'signed-in', config, session });
      go({ name: 'signed-out', config, idFailure: signInError === 'general' ? 'signInError' : idFailure === 'cancelled' ? undefined : idFailure, deleted: !!(config.desyrId && takeAfter('deleted')) });
    })();
    return () => void (live = false);
  }, [attempt]);

  /** After signing in (04, "After signing in"): the session kept, the page asked for, the splash. */
  const signedInNow = (config: Config, session: Session) => {
    signedInAccount(session);
    signedInDone();
    writeSession(session);
    sendSession();
    setSignedIn(true);
    guideOnHome(); // the install guide once on Home after a sign-in (32)
    guard(takeReturn()); // replacing the sign-in page, with the guard under it (07)
    setPhase({ name: 'signed-in', config, session });
  };

  /** "Open desyr": the circle a switch was going to before desyr ID's sign-in (36, Behaviour 6.4), else the default circle, else the first ready one (36). */
  const openCircles = (wanted = takeAfter('switch')?.mxid) => {
    circlesCard(false);
    const ready = [readSession()?.user_id, ...waiting().map((a) => a.user_id)].filter(Boolean) as string[];
    const to = ready.find((u) => u === wanted) ?? ready.find((u) => u === defaultAccount()) ?? ready[0];
    if (to && to !== readSession()?.user_id) switchTo(to, takeReturn());
    else reopen(takeReturn());
  };

  /** Right after "Opening your circles…" with no circle open yet: the default one opens in this page, without a second start (36, v1.58.15). */
  const openOpened = (config: Config) => {
    const wanted = takeAfter('switch')?.mxid;
    const list = waiting().filter((a) => !a.signed_out);
    const to = list.find((a) => a.user_id === wanted) ?? list.find((a) => a.user_id === defaultAccount()) ?? list[0];
    if (readSession() || !to || !activateAccount(to.user_id)) return openCircles(wanted);
    circlesCard(false);
    sendSession();
    setSignedIn(true);
    guideOnHome();
    guard(takeReturn());
    setPhase({ name: 'signed-in', config, session: readSession()! });
  };

  // A switch that ends anywhere but in the app (signed out, no storage, …): its screen goes (33).
  useEffect(() => void (phase.name !== 'starting' && phase.name !== 'signed-in' && endSwitching()), [phase.name]);

  switch (phase.name) {
    case 'starting':
      return <StartScreen />;
    case 'no-storage':
      return <NoStorage />;
    case 'config-failed':
      return <ConfigFailed failure={phase.failure} onRetry={() => (setPhase({ name: 'starting' }), setAttempt((a) => a + 1))} />;
    case 'offline':
      return <OfflineStart />;
    case 'signed-out':
      return <SignedOutRoutes config={phase.config} idFailure={phase.idFailure} deleted={phase.deleted} here={phase.here} render={signedOut} onSignedIn={(s) => signedInNow(phase.config, s)} />;
    case 'device': {
      // Cancel and "Ask for a new link" show the sign-in card at `/login`, never the automatic jump.
      const card = (here: boolean) => (history.replaceState(null, '', '/login'), setPhase({ name: 'signed-out', config: phase.config, here }));
      return <DevicePage config={phase.config} onCancel={() => card(false)} onNewLink={() => card(true)} />;
    }
    case 'auto':
      return <AutoSignIn id={phase.config.desyrId!} page={phase.page} onFailed={() => setPhase({ name: 'signed-out', config: phase.config, idFailure: 'failed' })} />;
    case 'opening':
      return (
        <Opening
          config={phase.config}
          onOpen={() => openOpened(phase.config)}
          onCircles={() => setPhase({ name: 'circles', config: phase.config })}
          onFailed={(why) => (circlesCard(false), setPhase({ name: 'signed-out', config: phase.config, idFailure: why }))}
        />
      );
    case 'circles':
      return <YourCircles config={phase.config} id={phase.config.desyrId!} signInError={phase.signInError} onOpen={() => openCircles()} onSignOut={() => void signOutOfId(phase.config)} extra={<Invitations config={phase.config} />} />;
    case 'signed-in': {
      // The hand-over is the start screen too (04, v1.58.48).
      return (
        <Boundary starting>
          <Suspense fallback={<StartScreen />}>
            <SignedIn session={phase.session} />
          </Suspense>
        </Boundary>
      );
    }
  }
}
